<template>
  <!-- Wrapper holds the class so scoped :deep(.el-dialog…) can match it as a
       descendant — el-dialog puts the class on .el-dialog itself (cf. DownloadAll). -->
  <div class="export-modal">
    <el-dialog
      :model-value="open"
      :show-close="false"
      :close-on-click-modal="!busy"
      :close-on-press-escape="!busy"
      width="372px"
      @update:model-value="onDialogToggle"
    >
      <template #header>
        <div class="export-modal__head">
          <span class="export-modal__icon">
            <UploadIcon />
          </span>
          <span class="export-modal__title">{{ t("exportPanel.title") }}</span>
          <span
            v-if="!busy"
            class="export-modal__close"
            role="button"
            :aria-label="t('exportPanel.cancel')"
            @click="close"
          >×</span>
        </div>
        <p class="export-modal__sub">{{ t("exportPanel.subtitle") }}</p>
      </template>

      <div class="export-modal__body" :class="{ 'is-busy': busy }">
        <template v-for="cat in categories" :key="cat">
          <div class="export-modal__group">{{ cat }}</div>
          <div
            v-for="item in itemsByCategory(cat)"
            :key="item.id"
            class="export-row"
            :class="{ 'is-disabled': item.state.type === 'disabled' }"
            role="checkbox"
            :aria-checked="selected.has(item.id)"
            :aria-disabled="item.state.type === 'disabled'"
            tabindex="0"
            @click="toggle(item)"
            @keydown.space.prevent="toggle(item)"
          >
            <span class="export-row__cb" :class="{ on: selected.has(item.id) }">
              <CheckIcon v-if="selected.has(item.id)" />
            </span>
            <span class="export-row__fic">
              <FileTypeIcon :format="item.format" />
            </span>
            <span class="export-row__meta">
              <span class="export-row__name">{{ item.name }}</span>
              <span class="export-row__desc" :class="{ reason: item.state.type === 'disabled' }">
                {{ item.state.type === "disabled" ? item.state.reason : item.description }}
              </span>
            </span>
            <span
              v-if="item.state.type === 'enabled'"
              class="export-row__dl"
              role="button"
              :title="t('exportPanel.download')"
              @click.stop="downloadOne(item)"
            >
              <DownloadIcon />
            </span>
          </div>
        </template>
      </div>

      <div v-if="busy" class="export-modal__progress">
        <div class="track" :class="{ indeterminate: progress === null }">
          <div class="bar" :style="progress !== null ? { width: progress + '%' } : undefined"></div>
        </div>
      </div>

      <template #footer>
        <div class="export-modal__foot">
          <button class="export-btn ghost" @click="cancel">
            {{ t("exportPanel.cancel") }}
          </button>
          <button class="export-btn green" :disabled="busy || selected.size === 0" @click="doExport">
            <span v-if="busy" class="export-spinner"></span>
            {{ footerLabel }}
          </button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { ElMessage } from "element-plus";
import { triggerDownload } from "./exportSources.js";
import { buildAndDownloadZip } from "./zipExport.js";
import { trackEvent } from "@/utils/analytics.js";
import UploadIcon from "@/components/icons/UploadIcon.vue";
import CheckIcon from "@/components/icons/CheckIcon.vue";
import DownloadIcon from "@/components/icons/DownloadIcon.vue";
import FileTypeIcon from "@/components/icons/FileTypeIcon.vue";

const props = defineProps({
  open: { type: Boolean, default: false },
  items: { type: Array, default: () => [] },
  projectId: { type: [Number, String], default: "" },
});
const emit = defineEmits(["update:open"]);

// Global scope so the modal follows the app's current locale and its
// lazily-loaded messages (en is loaded into the global scope only; a local
// scope would fall back to the bundled German).
const { t } = useI18n({ useScope: "global" });

const selected = ref(new Set());
const busy = ref(false);
const progress = ref(null); // null => indeterminate / hidden
let abortController = null;

const categories = computed(() => [...new Set(props.items.map((i) => i.category))]);
const itemsByCategory = (cat) => props.items.filter((i) => i.category === cat);

const footerLabel = computed(() => {
  const n = selected.value.size;
  if (busy.value) {
    return n > 1 ? t("exportPanel.generatingZip") : t("exportPanel.preparing");
  }
  if (n === 0) return t("exportPanel.exportSelected");
  return t("exportPanel.exportSelectedCount", { count: n });
});

function onDialogToggle(value) {
  if (!value) close();
}

function cancel() {
  if (busy.value) {
    if (abortController) abortController.abort();
    return; // stay open; doExport/downloadOne catch handles cleanup
  }
  close();
}

function close() {
  if (busy.value) return;
  if (abortController) abortController.abort();
  selected.value = new Set();
  emit("update:open", false);
}

function toggle(item) {
  if (busy.value || item.state.type === "disabled") return;
  const next = new Set(selected.value);
  next.has(item.id) ? next.delete(item.id) : next.add(item.id);
  selected.value = next;
}

async function downloadOne(item) {
  if (busy.value) return;
  busy.value = true;
  progress.value = null;
  abortController = new AbortController();
  try {
    const onProgress = (f) => (progress.value = Math.round(f * 100));
    const { filename, blob } = await item.getBlob({
      onProgress,
      signal: abortController.signal,
    });
    triggerDownload(blob, filename);
  } catch (e) {
    if (e.name !== "CanceledError" && e.name !== "AbortError") {
      ElMessage.error(t("exportPanel.errorOne", { name: item.name }));
    }
  } finally {
    busy.value = false;
    progress.value = null;
    abortController = null;
  }
}

async function doExport() {
  const ids = [...selected.value];
  if (ids.length === 0 || busy.value) return;
  const chosen = props.items.filter((i) => ids.includes(i.id));

  // Single selection → download raw, no zip.
  if (chosen.length === 1) {
    await downloadOne(chosen[0]);
    if (!busy.value) close();
    return;
  }

  // Multiple → fetch all, then zip.
  busy.value = true;
  progress.value = 0;
  abortController = new AbortController();
  const hasGlb = chosen.some((i) => i.id === "glb");
  let current = null;
  try {
    const files = [];
    const small = chosen.filter((i) => i.id !== "glb");
    const glb = chosen.find((i) => i.id === "glb");

    // Small files first — bump up to 10%.
    for (let i = 0; i < small.length; i++) {
      current = small[i];
      files.push(await small[i].getBlob({ signal: abortController.signal }));
      progress.value = Math.round(((i + 1) / small.length) * 10);
    }

    // GLB drives 10 → 95% (indeterminate if no Content-Length).
    if (glb) {
      current = glb;
      progress.value = null;
      files.push(
        await glb.getBlob({
          signal: abortController.signal,
          onProgress: (f) => (progress.value = 10 + Math.round(f * 85)),
        })
      );
    }
    if (!hasGlb) progress.value = 95;

    await buildAndDownloadZip(files, `project_${props.projectId}_export.zip`);
    trackEvent("export_zip_generated_ff", {
      project_id: props.projectId,
      file_ids: ids,
      file_count: ids.length,
    });
    progress.value = 100;
    busy.value = false;
    close();
  } catch (e) {
    busy.value = false;
    progress.value = null;
    if (e.name !== "CanceledError" && e.name !== "AbortError") {
      ElMessage.error(t("exportPanel.errorZip", { name: current ? current.name : "" }));
    }
  } finally {
    abortController = null;
  }
}
</script>

<style lang="scss" scoped>
.export-modal {
  :deep(.el-dialog) {
    background-color: var(--color-dark-bg-primary);
    border: 0.5px solid var(--color-border-white-alpha);
    border-radius: 6px;
    padding: 0;
  }
  :deep(.el-dialog__header),
  :deep(.el-dialog__body),
  :deep(.el-dialog__footer) {
    padding: 0;
  }
}
.export-modal__head {
  display: flex;
  align-items: center;
  padding: 14px 16px 4px;
}
.export-modal__icon {
  display: flex;
  color: var(--color-gree300);
  margin-right: 8px;
}
.export-modal__title {
  flex: 1;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-white);
}
.export-modal__close {
  cursor: pointer;
  color: var(--color-text-light);
  font-size: 20px;
  line-height: 1;
}
.export-modal__sub {
  margin: 0;
  padding: 0 16px 6px;
  font-size: 12px;
  color: var(--color-text-light);
}
.export-modal__body {
  padding: 2px 10px 6px;
  max-height: 52vh;
  overflow-y: auto;
  transition: opacity 0.2s;
  &.is-busy {
    opacity: 0.4;
    pointer-events: none;
  }
}
.export-modal__group {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  padding: 12px 10px 4px;
}
.export-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 8px;
  border-radius: 5px;
  cursor: pointer;
  &:hover {
    background: var(--color-dark-bg-secondary);
  }
  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.45;
    &:hover { background: transparent; }
  }
}
.export-row__cb {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid var(--color-text-secondary);
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-on-accent);
  &.on {
    background: var(--color-gree300);
    border-color: var(--color-gree300);
  }
}
.export-row__fic {
  flex: 0 0 auto;
  display: flex;
  color: var(--color-text-secondary);
}
.export-row__meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.export-row__name {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--color-white);
}
.export-row__desc {
  font-size: 11px;
  color: var(--color-text-light);
  margin-top: 2px;
  &.reason { font-style: italic; }
}
.export-row__dl {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  color: var(--color-text-light);
  flex: 0 0 auto;
  &:hover {
    color: var(--color-white);
    background: var(--color-dark-bg-tertiary);
  }
}
.export-modal__progress {
  padding: 4px 16px 0;
}
.export-modal__progress .track {
  height: 4px;
  border-radius: 3px;
  background: var(--color-dark-bg-tertiary);
  overflow: hidden;
  position: relative;
}
.export-modal__progress .bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 0;
  background: var(--color-gree300);
  border-radius: 3px;
  transition: width 0.2s linear;
}
.export-modal__progress .track.indeterminate .bar {
  width: 40%;
  animation: export-progress-indeterminate 1.1s ease-in-out infinite;
}
@keyframes export-progress-indeterminate {
  0% { left: -40%; }
  100% { left: 100%; }
}
.export-modal__foot {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding: 10px 16px 14px;
  border-top: 0.5px solid var(--color-border-dark);
}
.export-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 600;
  font-size: 13px;
  border-radius: 5px;
  padding: 7px 14px;
  border: 1px solid transparent;
  cursor: pointer;
  &.ghost {
    background: transparent;
    border-color: var(--color-border-white-alpha);
    color: var(--color-text-light);
    &:hover { color: var(--color-white); border-color: var(--color-white); }
  }
  &.green {
    background: var(--color-gree300);
    color: var(--color-on-accent);
    &:hover { filter: brightness(1.06); }
    &:disabled { opacity: 0.4; cursor: not-allowed; filter: none; }
  }
}
.export-spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid var(--color-on-accent);
  border-top-color: transparent;
  animation: export-spin 0.7s linear infinite;
}
@keyframes export-spin {
  to { transform: rotate(360deg); }
}
</style>
